/**
 * The frame every screen renders inside.
 *
 * It exists to make three requirements structural rather than a convention
 * somebody has to remember:
 *
 *   SHELL-077  exactly one `<h1>` per screen -- there is one here, and screens
 *              have no other way to render a heading.
 *   SHELL-015  the heading is focusable programmatically, with a stable id, so
 *              screen-transition.ts can find it without knowing which screen
 *              is mounted.
 *   SHELL-018  the document title is set from the same string as the heading,
 *              so the two cannot disagree.
 *
 * The `<main>` landmark is deliberately NOT here. It lives in App.tsx, outside
 * the router, because SHELL-078 requires the same element to survive a
 * navigation. A landmark rendered by the screen would be destroyed and rebuilt
 * on every route change.
 */

import type { ComponentChildren } from 'preact';
import { useLayoutEffect } from 'preact/hooks';

import { documentTitle } from '@shell/routes';
import { SCREEN_HEADING_ID } from '@shell/screen-transition';

interface ScreenProps {
  /** Used verbatim as the `<h1>` and, via `documentTitle`, as the page title. */
  title: string;
  children?: ComponentChildren;
}

export function Screen({ title, children }: ScreenProps) {
  // Layout effect rather than effect: the title is set before paint, so a fast
  // navigation never shows the previous screen's title in the tab.
  useLayoutEffect(() => {
    document.title = documentTitle(title);
  }, [title]);

  return (
    <>
      <h1 id={SCREEN_HEADING_ID} tabIndex={-1}>
        {title}
      </h1>
      {children}
    </>
  );
}
